/** Class of the layer every on-page marker is placed in. */
export const MARKER_LAYER_CLASS = 'brafton-marker-layer';

/**
 * The layer the widget's markers live in, and the one place that turns a marker's
 * stored page position into CSS.
 *
 * Marker positions are DOCUMENT coordinates (`clientX + scrollX`, `clientY + scrollY`).
 * An absolutely positioned element is not placed against the document, though — it is
 * placed against its containing block. For a marker appended straight to `<body>` that
 * is the document only while `<body>` is unpositioned and starts at the top-left of the
 * page. Many host sites break that: `body { position: relative }` together with a first
 * child's top margin collapsing through the body, or the WordPress admin bar's
 * `html { margin-top: 32px }`, push the body down, and every marker lands that far BELOW
 * where it was placed — vertically only, since the body still starts at the left edge.
 *
 * So markers go into a zero-size layer pinned to the body's top-left, and each position
 * is taken relative to wherever that layer actually sits. On a plain page the layer is
 * at (0, 0) and nothing changes; on an offset page the offset cancels out.
 */
export class MarkerLayer {
  /** The layer, created on first use and re-attached if the host page removed it. */
  static element(): HTMLElement {
    let layer = document.querySelector<HTMLElement>(`body > .${MARKER_LAYER_CLASS}`);
    if (!layer) {
      layer = document.createElement('div');
      layer.className = MARKER_LAYER_CLASS;
      // Zero-size and non-stacking (no z-index), so it never covers the host page and
      // each marker's own z-index still competes at the page level.
      layer.style.cssText = 'position:absolute;top:0;left:0;width:0;height:0;overflow:visible;';
      document.body.appendChild(layer);
    }
    return layer;
  }

  /** Adds a marker to the layer at a document position. */
  static place(marker: HTMLElement, pageX: number, pageY: number): void {
    const layer = MarkerLayer.element();
    layer.appendChild(marker);
    MarkerLayer.move(marker, pageX, pageY);
  }

  /** Sets a marker's CSS position for a document position, relative to the layer. */
  static move(marker: HTMLElement, pageX: number, pageY: number): void {
    const origin = MarkerLayer.origin();
    marker.style.left = `${Math.round(pageX - origin.x)}px`;
    marker.style.top = `${Math.round(pageY - origin.y)}px`;
  }

  /** Where the layer's top-left sits, in document coordinates. */
  private static origin(): { x: number; y: number } {
    const layerRect = MarkerLayer.element().getBoundingClientRect();
    return { x: layerRect.left + window.scrollX, y: layerRect.top + window.scrollY };
  }
}
